import React, { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import 'react-merge-refs';
import '@alfalab/hooks';
import './components/flag-icon/component.js';
import { _ as __rest, a as __assign } from './component-92fd3e2a.js';
import { getCountriesHash, getCountries } from '@alfalab/utils';
import { InputAutocomplete } from '../../input-autocomplete/esm';
import '../../select/esm';
import { CountriesSelect } from './components/select/component.js';
import { formatPhoneWithUnclearableCountryCode } from './utils/format-phone-with-unclearable-country-code.js';
import { useCaretAvoidCountryCode } from './useCaretAvoidCountryCode.js';
import { usePreventCaretReset } from './usePreventCaretReset.js';

var styles = {"addons":"intl-phone-input__addons_1xght","l":"intl-phone-input__l_1xght","xl":"intl-phone-input__xl_1xght"};
require('./index.css');

var countriesHash = getCountriesHash();
var MAX_DIAL_CODE_LENGTH = 4;
var IntlPhoneInput = forwardRef(function (_a, ref) {
    var _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.readOnly, readOnly = _c === void 0 ? false : _c, _d = _a.size, size = _d === void 0 ? 'm' : _d, _e = _a.colors, colors = _e === void 0 ? 'default' : _e, _f = _a.options, options = _f === void 0 ? [] : _f, _g = _a.countries, countries = _g === void 0 ? getCountries() : _g, _h = _a.clearableCountryCode, clearableCountryCode = _h === void 0 ? true : _h, className = _a.className, value = _a.value, onChange = _a.onChange, onCountryChange = _a.onCountryChange, _j = _a.defaultCountryIso2, defaultCountryIso2 = _j === void 0 ? 'ru' : _j, preventFlip = _a.preventFlip, inputProps = _a.inputProps, restProps = __rest(_a, ["disabled", "readOnly", "size", "colors", "options", "countries", "clearableCountryCode", "className", "value", "onChange", "onCountryChange", "defaultCountryIso2", "preventFlip", "inputProps"]);
    var _k = useState(defaultCountryIso2.toLowerCase()), countryIso2 = _k[0], setCountryIso2 = _k[1];
    var inputRef = useRef(null);
    var _l = useState(null), inputWrapperRef = _l[0], setInputWrapperRef = _l[1];
    var phoneLibUtils = useRef();
    var setCountryByIso2 = useCallback(function (iso2) {
        var country = countriesHash[iso2];
        var inputValue = "+" + country.dialCode;
        onChange(inputValue);
        setCountryIso2(country.iso2);
        return country;
    }, [onChange]);
    var setValue = useCallback(function (inputValue) {
        var newValue = inputValue;
        if (phoneLibUtils.current) {
            var Utils = phoneLibUtils.current;
            var utils = new Utils(countryIso2.toUpperCase());
            newValue = utils.input(inputValue);
        }
        onChange(newValue);
    }, [countryIso2, onChange]);
    var handleCountryChange = useCallback(function (countryCode) {
        if (onCountryChange) {
            onCountryChange(countryCode.toUpperCase());
        }
    }, [onCountryChange]);
    var setCountryByDialCode = useCallback(function (inputValue) {
        for (var i = 0; i < countries.length; i++) {
            var country_1 = countries[i];
            if (new RegExp("^\\+" + country_1.dialCode).test(inputValue)) {
                // Сначала проверяем, если приоритет не указан
                if (country_1.priority === undefined) {
                    setValue(inputValue);
                    setCountryIso2(country_1.iso2);
                    handleCountryChange(country_1.iso2);
                    break;
                }
                // Если страна уже была выставлена через селект, и коды совпадают
                if (countryIso2 === country_1.iso2) {
                    setValue(inputValue);
                    setCountryIso2(country_1.iso2);
                    handleCountryChange(country_1.iso2);
                    break;
                    // Если не совпадают - выбираем по приоритету
                }
                else if (country_1.priority === 0) {
                    setValue(inputValue);
                    setCountryIso2(country_1.iso2);
                    handleCountryChange(country_1.iso2);
                    break;
                }
            }
        }
    }, [countries, countryIso2, setValue, handleCountryChange]);
    var loadPhoneUtils = useCallback(function () {
        // prettier-ignore
        return import(/* webpackChunkName: "libphonenumber" */ './libphonenumber-js.min-5193d589.js')
            .then(function (utils) {
            phoneLibUtils.current = utils.AsYouType;
        })
            .catch(function (error) { return "An error occurred while loading libphonenumber-js:\n" + error; });
    }, []);
    var handleInputChange = useCallback(function (event) {
        var targetValue = event.target.value;
        var country = countriesHash[countryIso2];
        if (clearableCountryCode) {
            var newValue = targetValue.length === 1 && targetValue !== '+'
                ? "+" + targetValue
                : targetValue;
            setValue(newValue);
            if (value.length < MAX_DIAL_CODE_LENGTH) {
                setCountryByDialCode(newValue);
            }
            return;
        }
        if (!clearableCountryCode) {
            var newValue = formatPhoneWithUnclearableCountryCode(targetValue, country);
            setValue(newValue);
            return;
        }
        setValue(targetValue);
    }, [clearableCountryCode, countryIso2, setCountryByDialCode, setValue, value.length]);
    var handleSelectChange = useCallback(function (_a) {
        var selected = _a.selected;
        if (selected) {
            var country_2 = setCountryByIso2(selected.value);
            var inputValue = "+" + country_2.dialCode;
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
            }
            handleCountryChange(country_2.iso2);
        }
    }, [setCountryByIso2, handleCountryChange]);
    var handleChange = useCallback(function (payload) {
        var selected = payload.selected;
        if (!selected)
            return;
        if (selected.key.length < MAX_DIAL_CODE_LENGTH) {
            setCountryByDialCode(selected.key);
        }
        setValue(selected.key);
    }, [setValue, setCountryByDialCode]);
    useEffect(function () {
        if (!phoneLibUtils.current) {
            loadPhoneUtils().then(function () {
                setCountryByDialCode(value);
            });
        }
    }, [countryIso2, loadPhoneUtils, setCountryByDialCode, value]);
    var country = countriesHash[countryIso2];
    var countryCodeLength = ("+" + country.dialCode).length;
    useCaretAvoidCountryCode({ inputRef: inputRef, countryCodeLength: countryCodeLength, clearableCountryCode: clearableCountryCode });
    usePreventCaretReset({ inputRef: inputRef, countryCodeLength: countryCodeLength, clearableCountryCode: clearableCountryCode });
    return (React.createElement(InputAutocomplete, __assign({}, restProps, { ref: ref, inputProps: __assign(__assign({}, inputProps), { ref: inputRef, wrapperRef: setInputWrapperRef, type: 'tel', colors: colors, className: cn(className, styles[size]), addonsClassName: styles.addons, leftAddons: countries.length > 1 && (React.createElement(CountriesSelect, { dataTestId: 'countries-select', disabled: disabled || readOnly, size: size, selected: countryIso2, countries: countries, onChange: handleSelectChange, fieldWidth: inputWrapperRef && inputWrapperRef.getBoundingClientRect().width, preventFlip: preventFlip })) }), closeOnSelect: true, onInput: handleInputChange, onChange: handleChange, options: options, disabled: disabled, readOnly: readOnly, size: size, className: className, value: value })));
});
IntlPhoneInput.defaultProps = {
    size: 'm',
    defaultCountryIso2: 'ru',
};

export { IntlPhoneInput };
